import React, { useState, useRef, useEffect } from "react"
import { scrollToElement } from "../utils/scrollToElement"
import { ButtonWrapper } from "../../shared/components/ButtonWrapper"
import { Typography, typographyTypes } from "../../shared/components/Typography"
import Icon from "../../shared/components/Icon"

interface MenuItem {
  label: string
  id: string
}

interface PopoverMenuProps {
  items: MenuItem[]
  scrollableContainerRef: React.RefObject<HTMLDivElement>
}

const PopoverMenu: React.FC<PopoverMenuProps> = ({
  items,
  scrollableContainerRef,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const firstItemRef = useRef<HTMLDivElement>(null)
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null)

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false)
      }
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false)
      }
    }

    document.addEventListener("keydown", handleEscape)
    document.addEventListener("mousedown", handleClickOutside)

    return () => {
      document.removeEventListener("keydown", handleEscape)
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const handleMenuItemClick = (id: string) => {
    scrollToElement(id, scrollableContainerRef)
    setIsMenuOpen(false)
  }

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    index: number,
  ) => {
    switch (event.key) {
      case "ArrowDown":
        setFocusedIndex((prevIndex) =>
          prevIndex !== null && prevIndex < items.length - 1
            ? prevIndex + 1
            : 0,
        )
        break
      case "ArrowUp":
        setFocusedIndex((prevIndex) =>
          prevIndex !== null && prevIndex > 0
            ? prevIndex - 1
            : items.length - 1,
        )
        break
      case "Enter":
        handleMenuItemClick(items[index].id)
        break
      default:
        break
    }
  }

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
    setFocusedIndex(null)
  }

  const handleMenuItemFocus = (index: number) => {
    setFocusedIndex(index)
  }

  return (
    <div className="relative inline-block !ml-[20px]" ref={menuRef}>
      <ButtonWrapper
        onClick={toggleMenu}
        aria-haspopup="true"
        aria-expanded={isMenuOpen}
        shouldWrapChildren={false}
        ariaDescription="Profile Panel Shortcuts"
        styleOverrides="popover-menu-button flex items-center rounded-md w-[42px] h-[42px]"
      >
        <Icon
          iconType="dotsVertical"
          styleOverrides="inline-flex"
          altText="Profile Panel Shortcuts"
        />
      </ButtonWrapper>
      {isMenuOpen && (
        <div
          className="absolute mt-2 w-56 bg-white shadow-lg rounded-md right-0 z-10"
          onMouseLeave={() => setFocusedIndex(null)}
        >
          {items.map((item, index) => (
            <div
              key={item.id}
              ref={index === 0 ? firstItemRef : null}
              className={`flex items-center py-2 px-4 cursor-pointer border-gray-300 ${index === focusedIndex ? "bg-primary-blue-light" : ""}`}
              onClick={() => handleMenuItemClick(item.id)}
              onMouseEnter={() => handleMenuItemFocus(index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              tabIndex={0}
              role="menuitem"
            >
              <Typography
                text={item.label}
                variant={typographyTypes.PANEL_ITEM}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default PopoverMenu
